import { useState } from 'react'

import { useActionless, useActions } from 'redux/hooks'
import { toggleModal as toggleModalAction } from 'redux/active/actions'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { useCanEditDeck } from 'redux/deck/selectors'

import SaveService from 'services/save.service'

import { DeckCardDragType } from 'types/draggables'
import { CardType, STACK } from 'types/deck'

import Icon from 'components/elements/Icon'
import DropZones from 'components/deckPage/dragAndDrop/DropZones'

import { getMultiSelectedUpdates, getNewCategories } from 'utils/CategoryChangeHelpers'

import styles from './fixedDropZones.module.scss'

const FixedDropZones = () => {
  const [isDragging, setIsDragging] = useState(false)

  const canEditDeck = useCanEditDeck()

  const [setActiveState] = useActionless(SET_ACTIVE_STATE)
  const [toggleModal] = useActions(toggleModalAction)

  const moveToCategory = (
    card: CardType,
    currentCategory: string,
    newCategory: string | null,
    multiSelectedIds?: Record<string, boolean>,
  ) => {
    if (multiSelectedIds) {
      const updatedCards = getMultiSelectedUpdates(multiSelectedIds, currentCategory, newCategory)

      return SaveService.save(updatedCards).then(() => setActiveState({ multiSelectedIds: {} }))
    }

    const updatedCategory = newCategory || card.categories[1] || card.types[0] || card.front?.types[0] || '😎'

    let updatedCategories = getNewCategories(card.categories, STACK.CUSTOM, currentCategory, updatedCategory)

    if (!newCategory && card.globalCategories) updatedCategories = [...card.globalCategories]

    const updatedCard = { ...card, categories: updatedCategories, qty: card.qty || 1 }

    SaveService.save(updatedCard)
  }

  const handleMakeNewCategory = ({ card, stackName, multiSelectedIds }: DeckCardDragType) =>
    toggleModal('newCategoryDrop', { card, sourceStack: stackName, multiSelectedIds })

  // Note that we're only watching the first one of these for canDrop changes since they'd all be the same
  return (
    <div className={`${styles.dropZoneContainer} ${isDragging ? styles.dropZoneContainerIsDragging : ''}`}>
      <DropZones
        onCanDropChange={setIsDragging}
        onDrop={drag => moveToCategory(drag.card, drag.stackName || '😎', null, drag.multiSelectedIds)}
        checkCanDrop={() => canEditDeck}
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneIsDragging : ''} `}
        isOverClassName={styles.isHovered}>
        <Icon name="add" />
        Auto
      </DropZones>
      <DropZones
        onDrop={handleMakeNewCategory}
        checkCanDrop={() => canEditDeck}
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneIsDragging : ''} `}
        isOverClassName={styles.isHovered}>
        <Icon name="add" />
        New Category
      </DropZones>
      <DropZones
        onDrop={drag => moveToCategory(drag.card, drag.stackName || '😎', 'Maybeboard', drag.multiSelectedIds)}
        checkCanDrop={() => canEditDeck}
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneIsDragging : ''} `}
        isOverClassName={styles.isHovered}>
        <Icon name="add" />
        Maybeboard
      </DropZones>
      <DropZones
        onDrop={drag => moveToCategory(drag.card, drag.stackName || '😎', 'Sideboard', drag.multiSelectedIds)}
        checkCanDrop={() => canEditDeck}
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneIsDragging : ''} `}
        isOverClassName={styles.isHovered}>
        <Icon name="add" />
        Sideboard
      </DropZones>
    </div>
  )
}

export default FixedDropZones
